<template>
  <div class="relative">
    <!-- Trigger Button -->
    <button
      @click="toggleDropdown"
      class="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
    >
      <!-- Avatar -->
      <div class="relative">
        <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
          <span class="text-white font-semibold text-sm">
            {{ userInitials }}
          </span>
        </div>
        <!-- Online Status Indicator -->
        <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
      </div>
      
      <!-- User Info -->
      <div class="text-left">
        <p class="text-sm font-semibold text-gray-900">{{ authStore.user?.name }}</p>
        <div class="flex items-center">
          <p class="text-xs text-gray-500">Cuota: {{ authStore.quotaInfo.remaining }}/{{ authStore.quotaInfo.total }}</p>
          <div v-if="quotaChanged" class="ml-2 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        </div>
      </div>
      
      <!-- Dropdown Arrow -->
      <svg 
        class="w-4 h-4 text-gray-400 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <div 
      v-if="isOpen"
      class="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50"
    >
      <!-- User Info Header -->
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="flex items-center space-x-3">
          <div class="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
            <span class="text-white font-semibold">
              {{ userInitials }}
            </span>
          </div>
          <div>
            <p class="font-semibold text-gray-900">{{ authStore.user?.name }}</p>
            <p class="text-sm text-gray-500">{{ authStore.user?.email }}</p>
          </div>
        </div>
      </div>

      <!-- Quota Progress -->
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="flex justify-between items-center mb-2">
          <span class="text-sm font-medium text-gray-700">Cuota Mensual</span>
          <span class="text-sm text-gray-500">{{ authStore.quotaInfo.remaining }}/{{ authStore.quotaInfo.total }}</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div 
            class="h-2 rounded-full transition-all duration-300"
            :class="quotaChanged ? 'bg-gradient-to-r from-green-500 to-green-600 animate-pulse' : 'bg-gradient-to-r from-green-500 to-green-600'"
            :style="{ width: `${quotaPercentage}%` }"
          ></div>
        </div>
        <p class="text-xs text-gray-500 mt-1">{{ quotaPercentage }}% utilizada</p>
      </div>

      <!-- Menu Items -->
      <div class="py-2">
        <router-link 
          to="/reports" 
          class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          @click="closeDropdown"
        >
          <svg class="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          Mis Reportes
        </router-link>
        
        <button 
          v-if="authStore.isAdmin"
          @click="goToAdmin"
          class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <svg class="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
          Panel de Administración
        </button>
      </div>

      <!-- Logout -->
      <div class="border-t border-gray-100 pt-2">
        <button 
          @click="handleLogout"
          class="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
        >
          <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
          </svg>
          Cerrar Sesión
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const router = useRouter();
const authStore = useAuthStore();
const isOpen = ref(false);

// Computed properties
const userInitials = computed(() => {
  const name = authStore.user?.name || 'Usuario';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
});

const quotaPercentage = computed(() => {
  const used = authStore.quotaInfo.total - authStore.quotaInfo.remaining;
  return Math.round((used / authStore.quotaInfo.total) * 100);
});

// Watch for quota changes to show visual feedback
const quotaChanged = ref(false);
const previousQuota = ref(authStore.quotaInfo.remaining);

watch(() => authStore.quotaInfo.remaining, (newQuota) => {
  if (previousQuota.value !== newQuota && previousQuota.value !== 0) {
    quotaChanged.value = true;
    setTimeout(() => {
      quotaChanged.value = false;
    }, 2000);
  }
  previousQuota.value = newQuota;
});

// Methods
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const goToAdmin = () => {
  closeDropdown();
  router.push('/admin');
};

const handleLogout = () => {
  closeDropdown();
  authStore.logout();
};

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.relative')) {
    isOpen.value = false;
  }
};

// Add event listener when component mounts
import { onMounted, onUnmounted } from 'vue';

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>